import { type ReactNode, useMemo } from 'react';

/**
 * Zobrazení naší vlastní dokumentace. Umí jen tu podmnožinu Markdownu, kterou
 * v dokumentech používáme (nadpisy, tabulky, seznamy, kód, citace, tučné,
 * odkazy) — na to nemá smysl tahat do projektu parser.
 */
export function MarkdownDoc({ source }: { source: string }) {
  const blocks = useMemo(() => parseBlocks(source), [source]);
  return <div className="max-w-4xl">{blocks}</div>;
}

/** Inline formátování: `kód`, **tučně**, [odkaz](url). */
function inline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /`([^`]+)`|\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null = re.exec(text);
  let i = 0;
  while (m !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyPrefix}-${i++}`;
    if (m[1] !== undefined) {
      out.push(
        <code key={key} className="rounded bg-bg px-1.5 py-0.5 font-mono text-[13px] text-text">
          {m[1]}
        </code>,
      );
    } else if (m[2] !== undefined) {
      out.push(
        <strong key={key} className="font-semibold text-text">
          {m[2]}
        </strong>,
      );
    } else if (m[3] !== undefined && m[4] !== undefined) {
      out.push(
        <a
          key={key}
          href={m[4]}
          className="text-primary hover:underline"
          target={m[4].startsWith('http') ? '_blank' : undefined}
          rel="noopener noreferrer"
        >
          {m[3]}
        </a>,
      );
    }
    last = m.index + m[0].length;
    m = re.exec(text);
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Rozdělí řádek tabulky na buňky (bez krajních svislítek). */
function cells(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim());
}

function parseBlocks(source: string): ReactNode[] {
  const lines = source.split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  let key = 0;
  const next = () => `b${key++}`;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    // Prázdné řádky a vodorovné čáry
    if (!line.trim()) {
      i++;
      continue;
    }
    if (/^---+$/.test(line.trim())) {
      out.push(<hr key={next()} className="my-6 border-border-light" />);
      i++;
      continue;
    }

    // Blok kódu
    if (line.startsWith('```')) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? '').startsWith('```')) {
        body.push(lines[i] ?? '');
        i++;
      }
      i++;
      out.push(
        <pre
          key={next()}
          className="mb-4 overflow-x-auto rounded-lg bg-bg px-4 py-3 font-mono text-[13px] text-text"
        >
          {body.join('\n')}
        </pre>,
      );
      continue;
    }

    // Nadpisy
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1]?.length ?? 1;
      const text = inline(h[2] ?? '', next());
      const cls =
        level === 1
          ? 'text-2xl font-bold text-text mb-3'
          : level === 2
            ? 'text-xl font-bold text-text mt-8 mb-3'
            : 'text-base font-bold text-text mt-6 mb-2';
      out.push(
        level === 1 ? (
          <h1 key={next()} className={cls}>
            {text}
          </h1>
        ) : level === 2 ? (
          <h2 key={next()} className={cls}>
            {text}
          </h2>
        ) : (
          <h3 key={next()} className={cls}>
            {text}
          </h3>
        ),
      );
      i++;
      continue;
    }

    // Tabulka (druhý řádek je oddělovač)
    if (line.trim().startsWith('|') && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1] ?? '')) {
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('|')) {
        rows.push(cells(lines[i] ?? ''));
        i++;
      }
      out.push(
        <div key={next()} className="mb-5 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
                {head.map((cell) => (
                  <th key={cell} className="px-3 py-2">
                    {inline(cell, next())}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.join('|')} className="border-t border-border-light align-top">
                  {row.map((cell, ci) => (
                    <td key={`${head[ci] ?? ci}`} className="px-3 py-2 text-text">
                      {inline(cell, next())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Citace
    if (line.startsWith('> ')) {
      const body: string[] = [];
      while (i < lines.length && (lines[i] ?? '').startsWith('> ')) {
        body.push((lines[i] ?? '').slice(2));
        i++;
      }
      out.push(
        <blockquote
          key={next()}
          className="mb-4 border-l-4 border-primary bg-primary-25 px-4 py-2 text-sm text-text"
        >
          {inline(body.join(' '), next())}
        </blockquote>,
      );
      continue;
    }

    // Seznamy (odrážkové i číslované, včetně víceřádkových položek)
    const bullet = /^\s*([-*])\s+(.*)$/.exec(line);
    const numbered = /^\s*(\d+)\.\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      const ordered = !!numbered;
      const items: string[] = [];
      while (i < lines.length) {
        const cur = lines[i] ?? '';
        const b = /^\s*([-*])\s+(.*)$/.exec(cur);
        const n = /^\s*(\d+)\.\s+(.*)$/.exec(cur);
        if (b || n) {
          items.push((b?.[2] ?? n?.[2] ?? '').trim());
          i++;
          continue;
        }
        // Pokračovací řádek odsazené položky.
        if (cur.startsWith('  ') && cur.trim() && items.length > 0) {
          items[items.length - 1] = `${items[items.length - 1]} ${cur.trim()}`;
          i++;
          continue;
        }
        break;
      }
      const content = items.map((it) => (
        <li key={it} className="mb-1">
          {inline(it, next())}
        </li>
      ));
      out.push(
        ordered ? (
          <ol key={next()} className="mb-4 list-decimal pl-6 text-sm text-text-secondary">
            {content}
          </ol>
        ) : (
          <ul key={next()} className="mb-4 list-disc pl-6 text-sm text-text-secondary">
            {content}
          </ul>
        ),
      );
      continue;
    }

    // Odstavec
    const para: string[] = [];
    while (i < lines.length && (lines[i] ?? '').trim() && !/^[#>|`-]|^\d+\./.test(lines[i] ?? '')) {
      para.push(lines[i] ?? '');
      i++;
    }
    if (para.length === 0) {
      para.push(line);
      i++;
    }
    out.push(
      <p key={next()} className="mb-4 text-sm leading-relaxed text-text-secondary">
        {inline(para.join(' '), next())}
      </p>,
    );
  }

  return out;
}
